import type { MatchLog, MatchSetup } from '@sd/bots';
import type { Command, GameEvent, GameState } from '@sd/sim';
import type { Sample } from './history';
import type { FromWorker, ToWorker } from './protocol';

export interface Snapshot {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
  readonly history?: readonly Sample[];
}

/** The page's handle on the simulation worker: one per match. */
export class MatchHost {
  private readonly worker = new Worker(new URL('./sim.worker.ts', import.meta.url), {
    type: 'module',
  });
  private pendingLog: ((log: MatchLog) => void) | null = null;

  constructor(
    setup: MatchSetup,
    onSnapshot: (snapshot: Snapshot) => void,
    onError: (message: string) => void,
    skipTo = 0,
  ) {
    this.worker.addEventListener('message', (event: MessageEvent<FromWorker>) => {
      const message = event.data;
      if (message.type === 'snapshot') onSnapshot(message);
      else if (message.type === 'log') this.pendingLog?.(message.log);
      else onError(message.message);
    });
    this.worker.addEventListener('error', (event) => onError(event.message));
    this.post({ type: 'start', setup, ...(skipTo > 0 ? { skipTo } : {}) });
  }

  send(command: Command) {
    this.post({ type: 'command', command });
  }

  freeze(frozen: boolean) {
    this.post({ type: 'freeze', frozen });
  }

  setSpeed(speed: number) {
    this.post({ type: 'speed', speed });
  }

  log(): Promise<MatchLog> {
    return new Promise((resolve) => {
      this.pendingLog = resolve;
      this.post({ type: 'log' });
    });
  }

  dispose() {
    this.worker.terminate();
  }

  private post(message: ToWorker) {
    this.worker.postMessage(message);
  }
}
